import { useCallback, useState } from 'react';
import { GrainKey, PagerColumn, TableSortState } from '../constants/onePagerConstants';
import { OnePagerFilters } from '../types/onePagerTypes';
import { buildOnePagerRequest } from '../utils/onePagerRequestMapper';
import { fetchOnePagerAllPages } from '../services/onePagerApi';
import { mapOnePagerResponse } from '../utils/onePagerResponseMapper';
import { sortGrainRows } from '../utils/grainLogic';
import { buildWorkbook } from '../utils/onePagerWorkbookBuilder';
import { downloadWorkbook, WorkbookDownloadError } from '../utils/workbookDownload';

export type OnePagerExportErrorCategory =
	| 'API_FETCH_FAILED'
	| 'NO_DATA'
	| 'WORKBOOK_BUILD_FAILED'
	| 'DOWNLOAD_BLOCKED';

export class OnePagerExportError extends Error {
	readonly category: OnePagerExportErrorCategory;

	constructor(category: OnePagerExportErrorCategory, message: string) {
		super(message);
		this.name = 'OnePagerExportError';
		this.category = category;
	}
}

interface ExportSnapshot {
	filters: OnePagerFilters;
	grainSelections: GrainKey[];
	sort: TableSortState;
	columns: PagerColumn[];
	expandedRows?: string[];
}

export interface UseOnePagerExportOptions {
	defaultPageSize?: number;
}

const sanitizeToken = (value: string): string =>
	value
		.trim()
		.replace(/\s+/g, '_')
		.replace(/[^A-Za-z0-9_\-]/g, '')
		.replace(/_+/g, '_');

const getScopeToken = (filters: OnePagerFilters): string => {
	if (filters.location.length > 0) return `Location_${sanitizeToken(filters.location[0])}`;
	if (filters.market.length > 0) return `Market_${sanitizeToken(filters.market[0])}`;
	if (filters.region.length > 0) return `Region_${sanitizeToken(filters.region[0])}`;
	return 'National';
};

const buildTimestampedFileName = (filters: OnePagerFilters, now: Date): string => {
	const yyyy = now.getFullYear();
	const mm = String(now.getMonth() + 1).padStart(2, '0');
	const dd = String(now.getDate()).padStart(2, '0');
	const hh = String(now.getHours()).padStart(2, '0');
	const min = String(now.getMinutes()).padStart(2, '0');
	const ss = String(now.getSeconds()).padStart(2, '0');
	const scope = getScopeToken(filters);
	return `One_Pager_${scope}_${yyyy}-${mm}-${dd}_${hh}${min}${ss}.xlsx`;
};

export const useOnePagerExport = (options: UseOnePagerExportOptions = {}) => {
	const { defaultPageSize = 250 } = options;
	const [isExporting, setIsExporting] = useState(false);

	const exportOnePager = useCallback(async (snapshot: ExportSnapshot) => {
		if (isExporting) {
			return;
		}

		setIsExporting(true);

		const immutableSnapshot: ExportSnapshot = {
			filters: { ...snapshot.filters },
			grainSelections: [...snapshot.grainSelections],
			sort: { ...snapshot.sort },
			columns: [...snapshot.columns],
			expandedRows: snapshot.expandedRows ? [...snapshot.expandedRows] : undefined,
		};

		try {
			const exportRequest = buildOnePagerRequest({
				chartConfiguration: { grainSelections: immutableSnapshot.grainSelections },
				filters: immutableSnapshot.filters,
				sort: immutableSnapshot.sort,
				page: 1,
				size: defaultPageSize,
			});

			let dto;
			try {
				dto = await fetchOnePagerAllPages(exportRequest);
			} catch (error: any) {
				throw new OnePagerExportError(
					'API_FETCH_FAILED',
					error?.message || 'Failed to fetch One Pager export data.'
				);
			}

			const mapped = mapOnePagerResponse(dto);
			if (!mapped.rows || mapped.rows.length === 0) {
				throw new OnePagerExportError('NO_DATA', 'No data available for export.');
			}

			const orderedRows = sortGrainRows(
				mapped.rows,
				immutableSnapshot.sort,
				immutableSnapshot.grainSelections
			);

			let workbook;
			try {
				workbook = buildWorkbook({
					sheetName: 'One Pager',
					groupedRows: orderedRows,
					grandTotal: mapped.grandTotalRow,
					columns: immutableSnapshot.columns,
					grainSelections: immutableSnapshot.grainSelections,
				});
			} catch (error: any) {
				throw new OnePagerExportError(
					'WORKBOOK_BUILD_FAILED',
					error?.message || 'Failed to build workbook.'
				);
			}

			try {
				const filename = buildTimestampedFileName(immutableSnapshot.filters, new Date());
				await downloadWorkbook(workbook, filename);
			} catch (error: any) {
				if (error instanceof WorkbookDownloadError) {
					throw new OnePagerExportError('DOWNLOAD_BLOCKED', error.message);
				}
				throw new OnePagerExportError(
					'DOWNLOAD_BLOCKED',
					error?.message || 'Failed to download workbook.'
				);
			}
		} finally {
			setIsExporting(false);
		}
	}, [defaultPageSize, isExporting]);

	return {
		isExporting,
		exportOnePager,
	};
};
